import { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { supabase } from "../supabase";
import AdminVideos from "./AdminVideos";
import AdminLinks from "./AdminLinks";
import AdminNewLink, { NewLinkSeed } from "./AdminNewLink";
import AdminAvatar from "./AdminAvatar";
import AdminEffects from "./AdminEffects";

type Platform = "videolink" | "crm" | "avatar";
type Tab = "videos" | "links" | "new" | "effects";

const PLATFORMS: { id: Platform; label: string }[] = [
  { id: "videolink", label: "VideoLink" },
  { id: "crm", label: "CRM" },
  { id: "avatar", label: "Avatar video" },
];

const TABS: { id: Tab; label: string }[] = [
  { id: "videos", label: "Videos" },
  { id: "links", label: "Links" },
  { id: "new", label: "New link" },
  { id: "effects", label: "Effets" },
];

export default function Admin() {
  const nav = useNavigate();
  const loc = useLocation();
  const sp = new URLSearchParams(loc.search);
  const initialPlatform = (sp.get("app") || "videolink") as Platform;
  const [platform, setPlatform] = useState<Platform>(
    PLATFORMS.some((p) => p.id === initialPlatform) ? initialPlatform : "videolink"
  );

  const initial = (loc.hash.replace("#", "") as Tab) || "videos";
  const [tab, setTab] = useState<Tab>(
    TABS.some((t) => t.id === initial) ? initial : "videos"
  );

  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [seed, setSeed] = useState<NewLinkSeed | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav("/admin/login", { replace: true });
      else setUserEmail(data.session.user.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session) nav("/admin/login", { replace: true });
      else setUserEmail(session.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, [nav]);

  const selectPlatform = (p: Platform) => {
    setPlatform(p);
    const next = new URLSearchParams(loc.search);
    next.set("app", p);
    // Keep hash for VideoLink sub-tabs; clear it for other platforms.
    const hash = p === "videolink" ? window.location.hash : "";
    nav(`/admin?${next.toString()}${hash}`, { replace: true });
  };

  const selectTab = (t: Tab) => {
    setTab(t);
    window.location.hash = `#${t}`;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    nav("/admin/login", { replace: true });
  };

  if (platform === "crm") {
    return <Navigate to="/admin/crm" replace />;
  }

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-stone bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 md:py-4">
          {/* Row 1: logo + user — always single row */}
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-xl font-bold tracking-tight">Admin</h1>
            <div className="flex items-center gap-3 text-sm min-w-0">
              {userEmail && (
                <span className="text-concrete truncate hidden sm:inline">
                  {userEmail}
                </span>
              )}
              <button
                onClick={logout}
                className="text-concrete hover:text-charcoal whitespace-nowrap"
              >
                Sign out
              </button>
            </div>
          </div>

          {/* Row 2: platform menu */}
          <nav className="mt-3 flex gap-1 -mb-px">
            {PLATFORMS.map((p) => (
              <button
                key={p.id}
                onClick={() => selectPlatform(p.id)}
                className={`flex-1 sm:flex-initial px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition ${
                  platform === p.id
                    ? "bg-charcoal text-ivory"
                    : "text-concrete hover:text-charcoal hover:bg-ivory"
                }`}
              >
                {p.label}
              </button>
            ))}
          </nav>

          {/* Row 3: VideoLink sub-tabs */}
          {platform === "videolink" && (
            <nav className="mt-2 flex gap-1 -mb-px">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => selectTab(t.id)}
                  className={`flex-1 sm:flex-initial px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition ${
                    tab === t.id
                      ? "bg-white border border-stone shadow-sm"
                      : "text-concrete hover:text-charcoal hover:bg-ivory"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6">
        {platform === "avatar" && (
          <p className="text-sm text-concrete mb-6 max-w-2xl leading-relaxed">
            Créateur d&apos;avatar — même entrée que depuis la page d&apos;accueil. Choix du
            personnage (aperçu photo), fond, voix et langues (plusieurs possibles), puis génération
            du lien d&apos;enregistrement.
          </p>
        )}

        {platform === "videolink" && (
          <>
            {tab === "videos" && <AdminVideos />}
            {tab === "links" && (
              <AdminLinks
                onCreateNew={() => {
                  setSeed(null);
                  selectTab("new");
                }}
                onDuplicate={(s) => {
                  setSeed(s);
                  selectTab("new");
                }}
              />
            )}
            {tab === "new" && (
              <AdminNewLink
                seed={seed}
                onCreated={() => {
                  setSeed(null);
                  selectTab("links");
                }}
              />
            )}
            {tab === "effects" && <AdminEffects />}
          </>
        )}

        {platform === "avatar" && (
          <AdminAvatar />
        )}
      </main>
    </div>
  );
}
